import { findingExportIncomplete, type V2Finding } from '../../domain/finding';
import { useT } from '../../i18n/i18n';
import { findingClassLabel, findingStatusLabel } from '../../i18n/labels';
import { Icon } from '../../ui/components';

// Compact finding cards for an area, an item or the Findings tab: title · class · draft /
// withdrawn / incomplete badges · photo count · priority · optional context line. Tap opens the
// real editor (never a copy of it).
export function FindingList(props: { findings: V2Finding[]; onOpen: (finding: V2Finding) => void; emptyText?: string; contextOf?: (finding: V2Finding) => string }) {
  const { t, tp } = useT();
  if (props.findings.length === 0) {
    return <p className="card-note">{props.emptyText ?? t('area.otherFindingsEmpty')}</p>;
  }
  return (
    <div className="finding-list">
      {props.findings.map((finding) => {
        const incomplete = finding.status !== 'rejected' && findingExportIncomplete(finding);
        return (
          <button
            key={finding.id}
            type="button"
            className={`finding-card finding-card-${finding.findingClass} ${finding.status === 'rejected' ? 'finding-card-rejected' : ''} ${finding.status === 'draft' ? 'finding-card-draft' : ''}`}
            onClick={() => props.onOpen(finding)}
            data-testid="finding-card"
            data-finding-status={finding.status}
          >
            <div className="finding-card-head">
              <span className="finding-title">{finding.title.length > 0 ? finding.title : t('finding.newTitle')}</span>
              <Icon name="chevron_right" className="finding-chevron" />
            </div>
            {props.contextOf ? <div className="finding-context">{props.contextOf(finding)}</div> : null}
            <div className="finding-meta">
              {/* OD-13: a voluntary observation is not a New York requirement and must read as what
                  it is, wherever it appears. */}
              {finding.voluntaryObservation ? (
                <span className="badge badge-muted" data-testid="finding-voluntary-tag">
                  {t('finding.voluntaryTag')}
                </span>
              ) : (
                <span className="badge badge-muted">{findingClassLabel(t, finding.findingClass)}</span>
              )}
              {finding.voluntaryObservation ? null : <span className="badge badge-muted">{t('priority.value', { n: finding.priority })}</span>}
              {finding.status !== 'confirmed' ? <span className="badge badge-warn">{findingStatusLabel(t, finding.status)}</span> : null}
              {incomplete ? <span className="badge badge-warn">{t('item.incomplete')}</span> : null}
              {finding.evidenceIds.length > 0 ? <span className="badge badge-muted">{tp('common.photosCount', finding.evidenceIds.length)}</span> : null}
            </div>
            {finding.observation.length > 0 ? <div className="finding-body">{finding.observation}</div> : null}
          </button>
        );
      })}
    </div>
  );
}
